import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Skeleton,
  Space
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { useState } from 'react';
import { blue } from '@ant-design/colors';
import ConfirmModal from '../ConfirmModal';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { authDelete, authGet, authPost, authPut } from '../../utils/authCalls';
import Wrapper from '../Wrapper';
import AddressFormItem from '../AddressFormItem';
import * as Sentry from '@sentry/react';
import useShopAttrs from '../../utils/hooks/useShopAttrs';
import { salesPointsQueryOptions } from '../../utils/queryOptions';
import { salesPointBaseSchema, SalesPointPreview } from '@next-is-free-monorepo/next-is-free-types';
import { apiV1 } from '../../utils/apiClient';
import CModal from '../CModal';
import { IconAdjustmentsDollar, IconBasketPlus, IconPlus, IconRefresh } from '@tabler/icons-react';
import { NActionIcon, NButton, NModal } from '@next-is-free-monorepo/nif-web-components';
import { DeleteIcon, MoreHorizontalIcon, PlusSignIcon, StoreIconFreeIcons } from '@hugeicons/core-free-icons';
import { planGetLimit } from '@next-is-free-monorepo/next-is-free-shared-utils';
import PlanGate from '../PlanGate';
import usePlanLimits from '../../utils/hooks/usePlanLimits';

const SalesPointsForm = () => {
  const [salesPointData, setSalesPointData] = useState<SalesPointPreview | undefined | 'new'>();
  /*    const [salesPointData, setSalesPointData] = useState<SalesPointWithDefault | 'new'>();*/
  const [form] = Form.useForm();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCreation, setConfirmCreation] = useState(false);

  const shopAttrs = useShopAttrs();
  const queryClient = useQueryClient();
  const { usedPlanLimits } = usePlanLimits();

  const salesPoints = useSuspenseQuery(salesPointsQueryOptions());


  const createNewSalePoint = {
    mutationFn: async () => {
      const values = form.getFieldsValue();
      await salesPointBaseSchema.parseAsync(values);
      await apiV1.post('/shop/sales-points', values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sales-points'] });
      message.success('Punto vendita aggiunto');
      setConfirmCreation(false);
      setSalesPointData(undefined);
    },
    onError: async (error: Error) => {
      Sentry.withScope((scope) => {
        const serializedFormData = JSON.parse(JSON.stringify(form.getFieldsValue()));
        scope.setExtra('formData', serializedFormData);
        Sentry.captureException(error);
      });
    }
  };

  const editSalePoint = useMutation({
    mutationFn: async () => {
      if (salesPointData && salesPointData !== 'new') {
        const values = form.getFieldsValue();
        await salesPointBaseSchema.parseAsync(values);
        await apiV1.put(`/shop/sales-points/${salesPointData.id}`, values);
      } else {
        throw new Error('No salesPointData');
      }
    },
    onSuccess: async () => {
      setSalesPointData(undefined);
      await queryClient.invalidateQueries({ queryKey: ['sales-points'] });
      message.success('Informazioni aggiornate');
    },
    onError: async (error: Error) => {
      message.error('Qualcosa è andato storto, riprova');
      Sentry.withScope((scope) => {
        const serializedFormData = JSON.parse(JSON.stringify(form.getFieldsValue()));
        scope.setExtra('formData', serializedFormData);
        Sentry.captureException(error);
      });
    }
  });

  const deleteSalePoint = {
    mutationFn: async () => {
      if (!salesPointData || salesPointData == 'new') {
        throw new Error('No salesPointData');
      }
      await apiV1.delete(`/shop/sales-points/${salesPointData.id}`);
    },
    onSuccess: async () => {
      setSalesPointData(undefined);
      setConfirmDelete(false);
      await queryClient.invalidateQueries({ queryKey: ['sales-points'] });
      message.success('Informazioni aggiornate');
    },
    onError: async (error: Error) => {
      Sentry.withScope((scope) => {
        scope.setExtra('salesPointData', salesPointData);
        Sentry.captureException(error);
      });
    }
  };

  async function onSubmitForm() {
    if (salesPointData === 'new') {
      setConfirmCreation(true);
      return;
    } else {
      editSalePoint.mutate();
    }
  }

  return (
    <div>
      <div className={'flex flex-col gap-4 mt-6'}>
        {salesPoints.data.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            className={'border p-4 rounded-2xl'}
          >
            <Space size={'middle'}>
              {!item.is_default && !shopAttrs.data.subscription_status ?
                <div><p style={{ fontSize: 16, fontWeight: 600 }}>+15€</p><p>/mese</p></div>
                :
                <ShopOutlined className={'hide-mob'}
                              style={{ fontSize: 25, color: item.is_default ? blue[4] : 'grey' }} />
              }

              <div>
                <h3 className={'font-bold'}>
                  {item.is_default && (
                    <p
                      style={{
                        fontSize: 12,
                        color: blue[4],
                        width: 'fit-content',
                        borderRadius: 10
                      }}
                    >
                      Principale
                    </p>
                  )}
                  {item.name}
                </h3>
                <p>{item.full_address}</p>
                {/*  <p>{!item.salesPointAddress.manual && item.salesPointAddress.selected.label}</p>*/}
              </div>
            </Space>
            <NActionIcon onClick={() => setSalesPointData(item)} size={'small'} color={'transparent'} hugeIcon={MoreHorizontalIcon}/>
          </div>
        ))}
        <div className={'flex justify-end pt-5'}>
          <PlanGate hideMask featureKey={'maxSalesPoints'} currentNumber={salesPoints.data.length}>
            <NButton
              disabled={salesPoints.data.length >= planGetLimit(shopAttrs.data.subscription_plan, 'maxSalesPoints')}
              color={'secondary'}
              onClick={() => setSalesPointData('new')}>
              {`${usedPlanLimits(salesPoints.data.length, 'maxSalesPoints')} Aggiungi punto vendita`}
            </NButton>
          </PlanGate>
        </div>
      </div>
      {salesPointData && (
        <NModal
          hugeIcon={StoreIconFreeIcons}
          color={(salesPointData !== 'new' && salesPointData.is_default) ? 'blue' : 'black'}
          width={450}
          confirmButtonText={'Salva'}
          onConfirm={() => form.submit()}
          loading={editSalePoint.isPending}
          onClose={() => {
            setSalesPointData(undefined);
            form.resetFields();
          }}
          footerLeftComponent={(salesPointData !== 'new' && !salesPointData.is_default) ?
            <NButton hugeIcon={DeleteIcon} loading={editSalePoint.isPending} onClick={() => setConfirmDelete(true)}
                     className={'bg-transparent text-red-600 hover:bg-red-100 hover:text-red-600'}
                     size={'small'}>Elimina</NButton> : <div />}
          open={!!salesPointData}
        >
          <Form
            initialValues={salesPointData !== 'new' ? salesPointData : {}}
            onFinish={onSubmitForm}
            form={form}
            layout={'vertical'}
            style={{ paddingTop: 30, paddingBottom: 10 }}
            preserve={false}
          >
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Inserisci il nome del punto vendita'
                }
              ]}
              required={false}
              name={'name'}
              label={'Nome del punto vendita'}
              preserve={false}
            >
              <Input size={'large'}
                     placeholder={'Nome del punto vendita'}
              />
            </Form.Item>
            <AddressFormItem size={'large'} name={'address'} form={form} />
          </Form>
          <ConfirmModal open={confirmDelete} setOpen={setConfirmDelete} mutationOptions={deleteSalePoint}
                        title={'Attenzione'} buttonText={'Annulla abbonamento'} danger={true}>
            <p>L'abbonamento rimarrà attivo fino al termine del periodo di fatturazione corrente e poi sarà
              eliminato automaticamente</p>
          </ConfirmModal>
          {/*    <ConfirmModal open={confirmCreation}
                        setOpen={setConfirmCreation}
                        title={'Aggiungi'} buttonText={'Aggiungi nuovo'} mutationOptions={createNewSalePoint}
                        danger={false}>
            <div><p style={{ fontSize: 25, fontWeight: 600 }}>+15€ <span
              style={{ fontSize: 16 }}>/mese + iva</span></p><p>Il tuo abbonamento verrà aggiornato</p>
            </div>
          </ConfirmModal>*/}
          <CModal open={confirmCreation} onClose={() => setConfirmCreation(false)} icon={<IconBasketPlus />}
                  color={'green'}
                  mutationOptions={createNewSalePoint} title={'Vuoi aggiornare il tuo abbonamento?'}
                  text={'Il punto vendita verrà aggiunto ad un costo di 15€ al mese + IVA'} />
        </NModal>
      )}
    </div>
  );
};
export default SalesPointsForm;
